/**
 * Portada según quién mira.
 *
 * Un visitante necesita que le expliquen el negocio; un proveedor que ya entró
 * necesita saber si tiene órdenes pendientes y llegar rápido a su catálogo.
 * Mostrarle a todos el mismo argumento de venta convierte la pantalla de
 * entrada en un trámite.
 *
 * Diferencia con la v1: allí las métricas se calculaban sobre el estado
 * completo en memoria. Acá salen de la PRIMERA PÁGINA de `GET /api/ordenes`,
 * que es lo que el alcance del backend deja ver a cada rol —un cliente las
 * suyas, un proveedor las que tocan sus productos, el equipo todas—. Por eso
 * cada cifra dice sobre cuántas órdenes está calculada: una métrica de página
 * presentada como total es exactamente el defecto que hundió los reportes de la
 * v1.
 */
import { Link } from 'react-router-dom'
import { useOrdenes, type Orden } from '../api/hooks'
import { useSesion } from '../auth/sesion'
import { useUnidadesCarrito } from '../carrito/estado'
import { Aviso, Metrica } from '../componentes/ui'
import {
  IconoCamion,
  IconoCatalogo,
  IconoFabrica,
  IconoOrden,
  IconoPanel,
} from '../componentes/icons'
import { money, num } from '../lib/format'

/** Estados en los que la orden todavía da trabajo a alguien. */
const ABIERTOS = new Set(['recibida', 'confirmada', 'en_preparacion', 'despachada'])

export function Inicio() {
  const { yo } = useSesion()
  if (!yo) return null

  switch (yo.rol) {
    case 'proveedor':
      return <InicioProveedor />
    case 'cliente':
      return <InicioCliente organizacion={yo.organizacion} />
    case 'admin':
    case 'operador':
      return <InicioEquipo />
    default:
      return (
        <Aviso tono="alerta" titulo="Su cuenta todavía no tiene acceso">
          Un administrador debe asignarle un rol y su empresa. Mientras tanto puede{' '}
          <Link to="/catalogo" className="text-marca-600 hover:underline">
            navegar el catálogo
          </Link>
          .
        </Aviso>
      )
  }
}

/* ------------------------------------------------------------------------- */

/** Lo que se puede decir de una página de órdenes sin mentir sobre el total. */
function resumir(ordenes: Orden[]) {
  const abiertas = ordenes.filter((o) => ABIERTOS.has(o.estado))
  return {
    cuenta: ordenes.length,
    abiertas,
    porConfirmar: ordenes.filter((o) => o.estado === 'recibida'),
    montoAbierto: abiertas.reduce((s, o) => s + o.total, 0),
    total: ordenes.reduce((s, o) => s + o.total, 0),
    contribucion: ordenes.reduce((s, o) => s + (o.interno?.margenContribucion ?? 0), 0),
  }
}

function InicioProveedor() {
  const { yo } = useSesion()
  const consulta = useOrdenes()
  const ordenes = consulta.data?.pages.flatMap((p) => p.datos) ?? []
  const r = resumir(ordenes)

  return (
    <div className="space-y-3">
      <Encabezado
        titulo={yo?.organizacion ?? 'Su empresa'}
        bajada="Publique productos, mantenga stock y precios, y siga las órdenes que le compran."
      />

      {consulta.isPending ? (
        <p className="text-[13px] text-acero-500">Cargando…</p>
      ) : (
        <div className="grid sm:grid-cols-3 gap-2">
          <Metrica
            etiqueta="Órdenes que lo incluyen"
            valor={num(r.cuenta)}
            detalle="las más recientes"
          />
          <Metrica
            etiqueta="Por preparar o en ruta"
            valor={num(r.abiertas.length)}
            acento={r.abiertas.length > 0 ? 'marca' : 'neutro'}
            detalle="sin entregar ni cancelar"
          />
          <Metrica
            etiqueta="Recién recibidas"
            valor={num(r.porConfirmar.length)}
            detalle="a la espera de confirmación"
          />
        </div>
      )}

      {!consulta.isPending && r.cuenta === 0 && (
        <Aviso tono="alerta" titulo="Todavía no le han comprado">
          Si sus productos aún no están en el catálogo, cárguelos desde su panel. Sin peso ni
          dimensiones no podemos cotizar el flete, así que esos datos son obligatorios.
        </Aviso>
      )}

      <div className="grid md:grid-cols-3 gap-3">
        <Acceso
          a="/proveedor/catalogo"
          Icono={IconoCatalogo}
          titulo="Cargar catálogo"
          detalle="Suba muchos productos de una vez con un archivo CSV."
        />
        <Acceso
          a="/proveedor"
          Icono={IconoFabrica}
          titulo="Mis productos"
          detalle="Publicar, editar precios y stock, y subir fichas técnicas."
        />
        <Acceso
          a="/mis-ordenes"
          Icono={IconoOrden}
          titulo="Órdenes recibidas"
          detalle="Qué le compraron, a qué faena va y para cuándo."
        />
      </div>
    </div>
  )
}

function InicioCliente({ organizacion }: { organizacion: string | null }) {
  const consulta = useOrdenes()
  const unidades = useUnidadesCarrito()
  const ordenes = consulta.data?.pages.flatMap((p) => p.datos) ?? []
  const r = resumir(ordenes)

  return (
    <div className="space-y-3">
      <Encabezado
        titulo={organizacion ?? 'Su empresa'}
        bajada="Un solo canal para sus compras menores: una orden, una factura y una entrega a faena."
      />

      {consulta.isPending ? (
        <p className="text-[13px] text-acero-500">Cargando…</p>
      ) : (
        <div className="grid sm:grid-cols-3 gap-2">
          <Metrica
            etiqueta="Órdenes en curso"
            valor={num(r.abiertas.length)}
            detalle="sin entregar ni cancelar"
          />
          <Metrica etiqueta="En el carrito" valor={num(unidades)} detalle="unidades por confirmar" />
          <Metrica
            etiqueta="Monto en curso"
            valor={money(r.montoAbierto)}
            detalle="IVA incluido, de las órdenes abiertas"
          />
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-3">
        <Acceso
          a="/catalogo"
          Icono={IconoCatalogo}
          titulo="Comprar"
          detalle="Precio, disponibilidad y plazo a su faena, sin cotizar."
        />
        <Acceso
          a="/carrito"
          Icono={IconoCamion}
          titulo="Carrito"
          detalle={
            unidades > 0
              ? `${num(unidades)} unidad(es) esperando. Elija la faena y confirme.`
              : 'Arme una orden con productos de distintos proveedores.'
          }
        />
        <Acceso
          a="/mis-ordenes"
          Icono={IconoOrden}
          titulo="Mis órdenes"
          detalle="Estado, plazo comprometido y seguimiento de cada compra."
        />
      </div>
    </div>
  )
}

function InicioEquipo() {
  const consulta = useOrdenes()
  const ordenes = consulta.data?.pages.flatMap((p) => p.datos) ?? []
  const r = resumir(ordenes)

  return (
    <div className="space-y-3">
      <Encabezado
        titulo="Administración interna"
        bajada="Márgenes, contribución por orden, homologación de proveedores y accesos."
      />

      {consulta.isPending ? (
        <p className="text-[13px] text-acero-500">Cargando…</p>
      ) : (
        <div className="grid sm:grid-cols-4 gap-2">
          <Metrica etiqueta="Órdenes recientes" valor={num(r.cuenta)} detalle="primera página" />
          <Metrica
            etiqueta="Por confirmar"
            valor={num(r.porConfirmar.length)}
            acento={r.porConfirmar.length > 0 ? 'marca' : 'neutro'}
            detalle="en estado recibida"
          />
          <Metrica
            etiqueta="Facturado"
            valor={money(r.total)}
            detalle="suma de las órdenes listadas"
          />
          {/*
            La contribución va en cobre y solo para el equipo: es la cifra donde
            está el negocio, y el backend solo la manda a quien puede verla.
          */}
          <Metrica
            etiqueta="Contribución"
            valor={money(r.contribucion)}
            acento="marca"
            detalle="de las órdenes listadas"
          />
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-3">
        <Acceso
          a="/admin"
          Icono={IconoPanel}
          titulo="Órdenes y márgenes"
          detalle="Contribución real por orden, márgenes por categoría y parámetros."
        />
        <Acceso
          a="/admin/usuarios"
          Icono={IconoFabrica}
          titulo="Usuarios y empresas"
          detalle="Dar de alta proveedores y clientes, y asignar accesos."
        />
        <Acceso
          a="/catalogo"
          Icono={IconoCatalogo}
          titulo="Ver el catálogo"
          detalle="Lo mismo que ve un comprador, con los precios vigentes."
        />
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------------- */

function Encabezado({ titulo, bajada }: { titulo: string; bajada: string }) {
  return (
    <div className="bg-acero-950 text-white rounded-md px-4 py-3.5">
      {/* `text-white` explícito: la regla global de h1 fija text-acero-950. */}
      <h1 className="text-[19px] font-semibold leading-tight text-white">{titulo}</h1>
      <p className="text-[13px] text-acero-300 mt-1 leading-relaxed max-w-2xl">{bajada}</p>
    </div>
  )
}

function Acceso({
  a,
  Icono,
  titulo,
  detalle,
}: {
  a: string
  Icono: typeof IconoCatalogo
  titulo: string
  detalle: string
}) {
  return (
    <Link to={a} className="panel p-4 block hover:border-marca-500 transition-colors group">
      <div className="flex items-center gap-2">
        <span className="grid place-items-center w-8 h-8 rounded-sm bg-acero-100 text-acero-700 group-hover:bg-marca-700 group-hover:text-white transition-colors">
          <Icono size={17} />
        </span>
        <span className="font-semibold text-[14px]">{titulo}</span>
      </div>
      <p className="text-[12.5px] text-acero-600 mt-2 leading-relaxed">{detalle}</p>
    </Link>
  )
}
